import { IconArrowLeft } from "@tabler/icons-react";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { api, ApiError, type Lang, type StartResult } from "../api";
import { Footer } from "../components/Footer";
import { CodeInput } from "../components/CodeInput";
import { ErrorNote, Field, Spinner } from "../components/ui";
import { ERROR_KEYS, LANG_NAMES, LANGS } from "../i18n";
import { navigate } from "../router";
import { useSession } from "../session";
import { rt } from "../residents-text";

const RESEND_SECONDS = 30;

export function SignInPage({ magicToken, prefillEmail, returnTo }: { magicToken?: string; prefillEmail?: string; returnTo?: string }) {
  const { brand, brandInfo, lang, setLang, setMe, t } = useSession();
  const [mode, setMode] = useState<"email" | "phone" | "whatsapp">("email");
  // Residents of the Azores sign in with a code on the WhatsApp they already use with us (docs/22 §12).
  const whatsappSignIn = brand?.code === "azores-getaways";
  const [address, setAddress] = useState(prefillEmail ?? "");
  const [challenge, setChallenge] = useState<StartResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [magicState, setMagicState] = useState<"idle" | "checking" | "failed">(magicToken ? "checking" : "idle");
  const [resendIn, setResendIn] = useState(0);
  const [codeKey, setCodeKey] = useState(0);
  const magicTried = useRef<string | null>(null);
  // Google and Apple, when their keys exist in the customers organisation; back from a failed attempt with ?signIn=failed.
  const socials = (["google", "apple"] as const).filter((p) => brandInfo?.signIn?.[p]);
  const socialFailed = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("signIn") === "failed";

  useEffect(() => {
    if (prefillEmail) setAddress(prefillEmail);
  }, [prefillEmail]);

  // A magic link lands here as /signin/:token: complete it and go home.
  useEffect(() => {
    if (!magicToken || magicTried.current === magicToken) return;
    magicTried.current = magicToken;
    setMagicState("checking");
    api
      .authMagic(magicToken)
      .then((res) => {
        setMe(res.me);
        navigate("/", { replace: true });
      })
      .catch(() => {
        setMagicState("failed");
        window.history.replaceState(null, "", "/signin");
      });
  }, [magicToken, setMe]);

  useEffect(() => {
    if (resendIn <= 0) return;
    const id = window.setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => window.clearTimeout(id);
  }, [resendIn]);

  const messageFor = (err: unknown) => {
    if (err instanceof ApiError) {
      const key = ERROR_KEYS[err.code];
      if (key) return t(key);
      if (err.status === 503) return t("errProviderUnavailable");
    }
    if (err instanceof TypeError) return t("offline");
    return t("somethingWrong");
  };

  const start = async (e?: FormEvent) => {
    e?.preventDefault();
    const value = address.trim();
    if (!value) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.authStart({ ...(mode === "email" ? { email: value } : { phone: value }), ...(mode === "whatsapp" ? { via: "whatsapp" as const } : {}), language: lang });
      setChallenge(res);
      setResendIn(RESEND_SECONDS);
      setCodeKey((k) => k + 1);
    } catch (err) {
      setError(messageFor(err));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (code: string) => {
    if (!challenge) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.authVerify({ challengeId: challenge.challengeId, code });
      setMe(res.me);
      navigate(returnTo ?? "/", { replace: true });
    } catch (err) {
      setError(messageFor(err));
      if (err instanceof ApiError && (err.code === "challenge_expired" || err.code === "too_many_attempts")) {
        setChallenge(null);
      } else {
        setCodeKey((k) => k + 1);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="page page-signin">
      <main className="signin">
        {brandInfo?.staging && (
          <div className="staging-banner">
            <span>{t(brandInfo.staging.simulatedChannels ? "stagingTest" : "stagingBanner")}</span>{" "}
            <a href={brandInfo.staging.staffUrl} target="_blank" rel="noopener noreferrer">
              {t("stagingOutbox")}
            </a>
          </div>
        )}
        {brand && <img src={brand.logoUrl} alt={brand.name} className="signin-logo" />}
        <h1 className="signin-title">{t("signInTitle")}</h1>
        <p className="signin-lead muted">{t("signInLead")}</p>

        {magicState === "checking" ? (
          <Spinner label={t("magicChecking")} />
        ) : challenge ? (
          <section className="card signin-card">
            <button type="button" className="link-button back-link" onClick={() => setChallenge(null)}>
              <IconArrowLeft size={16} /> {t("changeAddress")}
            </button>
            <p>{challenge.channel === "whatsapp" ? rt(lang, "codeSentWhatsapp", { to: challenge.to }) : t(challenge.channel === "sms" ? "codeSentSms" : "codeSentTo", { to: challenge.to })}</p>
            <p className="field-label">{t("enterCode")}</p>
            <CodeInput onComplete={(code) => void verify(code)} disabled={busy} resetKey={codeKey} length={challenge.codeLength ?? 6} />
            {busy && <p className="muted small">{t("verifying")}</p>}
            {error && <ErrorNote>{error}</ErrorNote>}
            <p className="muted small">{t("codeHint")}</p>
            <button type="button" className="link-button" disabled={resendIn > 0 || busy} onClick={() => void start()}>
              {resendIn > 0 ? t("resendIn", { s: resendIn }) : t("resend")}
            </button>
          </section>
        ) : (
          <form className="card signin-card" onSubmit={(e) => void start(e)}>
            {magicState === "failed" && <ErrorNote>{t("magicFailed")}</ErrorNote>}
            {socialFailed && <ErrorNote>{t("socialFailed", { provider: "Google / Apple" })}</ErrorNote>}
            {socials.length > 0 && (
              <>
                {socials.map((p) => (
                  <a key={p} className="button button-block social-button" href={`/api/v1/auth/social/${p}?${new URLSearchParams({ language: lang, ...(returnTo ? { returnTo } : {}) }).toString()}`}>
                    {t("continueWith", { provider: p === "google" ? "Google" : "Apple" })}
                  </a>
                ))}
                <p className="or-divider muted small">{t("orDivider")}</p>
              </>
            )}
            <Field label={mode === "email" ? t("signInEmail") : mode === "whatsapp" ? rt(lang, "whatsappNumber") : t("signInPhone")}>
              <input
                type={mode === "email" ? "email" : "tel"}
                inputMode={mode === "email" ? "email" : "tel"}
                autoComplete={mode === "email" ? "email" : "tel"}
                placeholder={mode === "email" ? t("emailPlaceholder") : t("phonePlaceholder")}
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                required
                autoFocus
              />
            </Field>
            {error && <ErrorNote>{error}</ErrorNote>}
            <button type="submit" className="button button-primary button-block" disabled={busy || !address.trim()}>
              {busy ? t("sending") : t("sendCode")}
            </button>
            <button
              type="button"
              className="link-button"
              onClick={() => {
                setMode(mode === "email" ? "phone" : "email");
                setAddress("");
                setError(null);
              }}
            >
              {mode === "email" ? t("usePhone") : t("useEmail")}
            </button>
            {whatsappSignIn && mode !== "whatsapp" && (
              <button
                type="button"
                className="link-button"
                onClick={() => {
                  setMode("whatsapp");
                  setAddress("");
                  setError(null);
                }}
              >
                {rt(lang, "whatsappSignIn")}
              </button>
            )}
          </form>
        )}

        <label className="lang-select">
          <span className="muted small">{t("language")}</span>
          <select value={lang} onChange={(e) => setLang(e.target.value as Lang)}>
            {LANGS.map((l) => (
              <option key={l} value={l}>
                {LANG_NAMES[l]}
              </option>
            ))}
          </select>
        </label>
      </main>
      <Footer />
    </div>
  );
}
